/**
 * Read `document.cookie` for the `linkhr_session=1` marker. CC-01 / D-05.
 *
 * The sentinel is a non-HttpOnly companion to the HttpOnly refresh cookie. Its
 * presence tells the frontend "a refresh token cookie exists — try a silent
 * refresh on boot". Its *value* is binary (`1`) and carries no identity.
 *
 * The pattern below matches only the whole cookie form — `linkhr_session=1`
 * preceded by start-of-string or `; ` (with optional whitespace) and followed
 * by end-of-string or `;`. This blocks substring collisions like
 * `x_linkhr_session=1` and `linkhr_sessionx=1`.
 *
 * Returns `false` when `document` is unavailable (SSR/test environments
 * without a DOM).
 */
const SENTINEL_PATTERN = /(?:^|;\s*)linkhr_session=1(?:;|$)/;

export function readSessionSentinel(): boolean {
  if (typeof document === "undefined") return false;
  return SENTINEL_PATTERN.test(document.cookie);
}

/**
 * Expire the `linkhr_session` sentinel client-side on logout.
 *
 * The backend sets the sentinel with `Domain=<shared-parent>` (e.g.
 * `staging.hr.linkco.jo`) so it's shared across tenant subdomains. Logout relies
 * on `POST /auth/logout`'s `Set-Cookie` to clear it — but that call is
 * best-effort and, on staging, currently 400s, leaving the sentinel behind. A
 * lingering sentinel makes `refreshOnLoad` attempt a silent refresh on the next
 * boot instead of short-circuiting to UNAUTH, which is the seam a stale session
 * could ride back in through. The sentinel is non-HttpOnly, so we expire it here
 * ourselves — host-only plus every registrable-parent suffix of the current
 * host, since we don't know which exact `Domain` the backend used. Non-matching
 * domains are ignored by the browser, so the sweep is safe.
 */
export function clearSessionSentinel(): void {
  if (typeof document === "undefined") return;
  const host = typeof location !== "undefined" ? location.hostname : "";
  const labels = host.split(".").filter((l) => l.length > 0);
  // host-only (no Domain attribute) first, then each parent suffix the host
  // belongs to — stop before the public suffix (last label) so we never try to
  // set a cookie on a TLD, which browsers reject.
  const domainAttrs = [""];
  for (let i = 0; i < labels.length - 1; i += 1) {
    domainAttrs.push(`; domain=${labels.slice(i).join(".")}`);
  }
  for (const domainAttr of domainAttrs) {
    document.cookie = `linkhr_session=; path=/; max-age=0${domainAttr}`;
  }
}
